// Client for the fieldline worker. Owns the worker lifecycle and matches
// async trace/contour requests to their resolvers by reqId so overlapping
// calls (and worker reinitialization during a dataset switch) can't deliver
// stale results into the wrong promise.

import type { PhysicsGrid } from '../physics/types';
import type {
  ContourClipPayload,
  PointXYZ,
  WireDomain,
  WorkerRequest,
  WorkerResponse,
} from './protocol';

export let isFieldlineReady = false;
let worker: Worker | null = null;
let nextReqId = 1;

type ResolveValue = Float32Array;
interface Pending {
  resolve: (v: ResolveValue) => void;
  reject: (err: unknown) => void;
  kind: 'trace' | 'contour';
}
const pending = new Map<number, Pending>();

const ABORTED = (): Error => new Error('aborted: worker reinitialized');

function rejectAllPending(err: unknown): void {
  pending.forEach(({ reject }) => { try { reject(err); } catch { /* swallow */ } });
  pending.clear();
}

// Minimal viewer-grid shape the worker init reads. PR 7/8 narrows this further.
interface ViewerGridShape {
  nx: number;
  ny: number;
  nz: number;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  zMin: number;
  zMax: number;
  resolutionX: number;
  resolutionY: number;
  resolutionZ: number;
}

function toPhysicsGrid(vgrid: ViewerGridShape): PhysicsGrid {
  return {
    nx: vgrid.nx, ny: vgrid.ny, nz: vgrid.nz,
    xmin: vgrid.xMin, ymin: vgrid.yMin, zmin: vgrid.zMin,
    xmax: vgrid.xMax, ymax: vgrid.yMax, zmax: vgrid.zMax,
    dx: vgrid.resolutionX, dy: vgrid.resolutionY, dz: vgrid.resolutionZ,
  };
}

function post(msg: WorkerRequest): void {
  if (!worker) throw new Error('Fieldline worker not initialized');
  worker.postMessage(msg);
}

export function initFieldlineWorker(
  viewerGrid: ViewerGridShape,
  Bx: Float32Array | null | undefined,
  By: Float32Array | null | undefined,
  Bz: Float32Array | null | undefined,
): Promise<void> {
  if (!Bx || !By || !Bz) return Promise.reject(new Error('initFieldlineWorker: missing Bx/By/Bz'));
  const grid = toPhysicsGrid(viewerGrid);
  return new Promise<void>((resolve, reject) => {
    try {
      if (worker) {
        try { worker.terminate(); } catch { /* swallow */ }
        rejectAllPending(ABORTED());
      }
      isFieldlineReady = false;
      worker = new Worker(new URL('./fieldlineWorker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (ev: MessageEvent<WorkerResponse>): void => {
        const msg = ev.data;
        if (!msg || typeof msg !== 'object') return;
        if (msg.type === 'inited') { isFieldlineReady = true; resolve(); return; }
        if (msg.type !== 'result' && msg.type !== 'contourResult') return;
        const entry = pending.get(msg.reqId);
        if (!entry) return;
        pending.delete(msg.reqId);
        const out = msg.type === 'result' ? msg.payload.points : msg.payload.positions;
        try { entry.resolve(out); } catch (e) { try { entry.reject(e); } catch { /* swallow */ } }
      };
      worker.onerror = (err): void => {
        isFieldlineReady = false;
        rejectAllPending(err);
        reject(err);
      };
      post({ type: 'init', payload: { grid, Bx, By, Bz } });
    } catch (e) { reject(e); }
  });
}

export function traceFieldLineAsync(start: PointXYZ, h: number, maxSteps: number, domain?: WireDomain): Promise<Float32Array> {
  return new Promise<Float32Array>((resolve, reject) => {
    if (!worker || !isFieldlineReady) { reject(new Error('Fieldline worker not ready')); return; }
    const reqId = nextReqId++;
    pending.set(reqId, { resolve, reject, kind: 'trace' });
    try {
      const payload: { start: PointXYZ; h: number; maxSteps: number; domain?: WireDomain } = { start, h, maxSteps };
      if (domain && domain.kind !== 'full') payload.domain = domain;
      post({ type: 'trace', reqId, payload });
    } catch (e) {
      pending.delete(reqId);
      reject(e);
    }
  });
}

export function generateContourAsync(
  component: string,
  isoValue: number,
  clip: ContourClipPayload | undefined,
): Promise<Float32Array> {
  return new Promise<Float32Array>((resolve, reject) => {
    if (!worker || !isFieldlineReady) { reject(new Error('Worker not ready')); return; }
    const reqId = nextReqId++;
    pending.set(reqId, { resolve, reject, kind: 'contour' });
    try {
      const payload: { component: string; isoValue: number; clip?: ContourClipPayload } = { component, isoValue };
      if (clip !== undefined) payload.clip = clip;
      post({ type: 'contour', reqId, payload });
    } catch (e) {
      pending.delete(reqId);
      reject(e);
    }
  });
}
